/**
 * IndicatorDescriptionParagraph.jsx
 * Renders description paragraphs with token replacement
 * Migration from front/src/components/IndicatorDescriptionParagraph.js
 */
import React, { memo } from 'react';
import { Typography } from '@mui/material';
import useTokenReplacement from '../hooks/ui/useTokenReplacement';
import { printTextSize } from '../utils/entityHelpers';

/**
 * Component to render description paragraphs with token replacement
 * Handles both single descriptions and arrays of descriptions recursively
 * @param {Object} props - Component props
 * @param {string|Array} props.description - Description text or array of descriptions
 * @param {Object} props.sx - Custom styling
 * @param {string} props.type - Entity type for token replacement context
 * @param {Object} props...rest - Additional props passed to Typography
 * @returns {React.ReactElement|Array} IndicatorDescriptionParagraph component(s)
 */
const IndicatorDescriptionParagraph = memo(({
  description = [],
  sx = {},
  type,
  ...rest
}) => {
  const replaceTokens = useTokenReplacement({ entityType: type });

  // If description is an Array → recurse!
  if (Array.isArray(description)) {
    return description.map((descriptionItem, index) => (
      <IndicatorDescriptionParagraph
        key={`${descriptionItem}-${index}`}
        description={descriptionItem}
        type={type}
        sx={sx}
        {...rest}
      />
    ));
  }

  return (
    <Typography
      gutterBottom
      sx={{
        ...printTextSize,
        ...sx
      }}
      {...rest}
    >
      {replaceTokens(description)}
    </Typography>
  );
});

IndicatorDescriptionParagraph.displayName = 'IndicatorDescriptionParagraph';

export default IndicatorDescriptionParagraph;
